import { SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Divider, Input, Space, Table, Typography } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import AuditLogButton from "../components/AuditLogButton";
import CloseProjectButton from "../components/Button/CloseProjectButton";
import CheckboxDropdown from "../components/CheckboxDropdown";
import AuditLogModal from "../components/Modal/AuditLogModal";
import Map from "../components/Overview/Map";
import SearchButton from "../components/SearchButton";
import SharepointComponent from "../components/SharepointComponent";
import TabButton from "../components/TabButton";
import useContainerDimensions from "../hooks/useContainerDimensions";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import usePermission from "../hooks/usePermission";
import { getOperations } from "../services/api-server/operations";
import { getProject } from "../services/api-server/projects";
import { createWellExaminationApi } from "../services/api-server/wellExamination";
import { usePermissionStore } from "../stores/permissionStore";
import { getWells } from "../services/api-server/wells";
import { getTenantStore } from "../stores/tenantStore";
import { TProjectData } from "../types/project";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import { setLookupData } from "../utils/lookupFinder";
import { HomeContext } from "./Home";
import MultiAssetProjectContainer from "./MultiAssetProjectContainer";
import NoAccess from "./NoAccess";
import WellExaminationProjectView from "./WellExaminationProjectView";

const { Text } = Typography;

// Each object represents a specific property or field for a project, with three attributes
const projectTemplateExample: any = [
	{ label: "Project title", key: "project_name", value: "name" },
	{ label: "Project type", key: "project_type", value: "type" },
	{ label: "Project area", key: "project_area", value: "area" },
	{ label: "Project code", key: "project_code", value: "ABC123456A" },
	{ label: "Owner/Operator", key: "client", value: "Addax Petroleum" },
	{ label: "Region", key: "region", value: "North Europe" },
	{ label: "Country", key: "country", value: "England" },
	{ label: "Operating office", key: "operating_office", value: "Aberdeen" },
];

function ProjectCampaignOverview(props: any) {
	const location = useLocation();
	const navigate = useNavigate();

	const tenantStore = getTenantStore();
	const currentTenant = tenantStore.getTenant();
	const currentTenantKey = currentTenant?.value;

	const projectCode =
		location?.state?.project_code || location?.pathname?.split("/")[2];

	const context: any = useContext(HomeContext);
	const [projectInfo, setProjectInfo] = useState<any>(null);
	const [projectTitle, setProjectTitle] = useState<any>(null);
	const [projectDetailsExpand, setProjectDetailsExpand] = useState<any>(false);
	const [wellList, setWellList] = useState<any>(null);
	const [selectedWell, setSelectedWell] = useState<any>(null);
	const [selectedWellInfo, setSelectedWellInfo] = useState<any>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [activePage, setActivePage] = useState<any>("kanban");
	const [taskList, setTaskList] = useState<any>(null);
	const [filteredTaskList, setFilteredTaskList] = useState<any>(null);

	const [tableRef, tableDimensions] = useContainerDimensions();

	//Filtering states
	const [filteredWellList, setFilteredWellList] = useState<any>(null);
	const [filterMode, setFilterMode] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<any>("");
	const [selectedFilters, setSelectedFilters] = useState<any>({
		location: [],
		well_purpose: [],
		well_status: [],
	});

	//Table states
	const [dataTable, setDataTable] = useState<any>(null);
	const [filteredDataTable, setFilteredDataTable] = useState<any>(null);
	const [tempRecord, setTempRecord] = useState<any>(null);
	const [searchRecord, setSearchRecord] = useState<any>([]);
	// const [tableHeight, setTableHeight] = useState<any>(null);

	//Audit Logs states
	const [auditOpen, setAuditOpen] = useState<any>(false);

	const { handleContextMenu } = useHandleContextMenu();
	const hasEditProjectPermission = usePermission(
		"Edit.Project",
		projectInfo?.project_code
	);
	const hasViewProjectPermission = usePermission(
		"View.Project",
		projectInfo?.project_code
	);
	const hasDeleteProjectPermission = usePermission(
		"Delete.Project",
		projectInfo?.project_code
	);

	// Well examination: examiners who are not on the team get the lean view;
	// team members get an extra tab on Wells projects.
	const permissionStore = usePermissionStore();
	const permissionsInitialized = permissionStore.isInitialized;
	const projectCodeForAccess = projectInfo?.project_code;
	const examinerOnlyView =
		!!projectCodeForAccess &&
		permissionStore.isWellExaminer(projectCodeForAccess) &&
		!permissionStore.hasTeamAccess(projectCodeForAccess);
	const showWellExamination = projectInfo?.project_area === "Wells";
	const [singleAssetTab, setSingleAssetTab] = useState<string>("overview");
	const [wellExaminationDisabled, setWellExaminationDisabled] =
		useState<boolean>(true);
	const wellExaminationApi = useMemo(
		() =>
			showWellExamination && currentTenantKey && projectCodeForAccess
				? createWellExaminationApi(currentTenantKey, projectCodeForAccess)
				: null,
		[showWellExamination, currentTenantKey, projectCodeForAccess]
	);

	const [panelRef, panelDimensions] = useContainerDimensions();

	const handleSearch = (record: any) => {
		setTempRecord(record);
	};

	useEffect(() => {
		if (searchRecord && tempRecord) {
			const recordIndex = searchRecord.findIndex((item: any) => {
				return item.columnKey === tempRecord.columnKey;
			});
			if (recordIndex !== -1) {
				if (tempRecord.value != "") {
					const _record = [...searchRecord];
					_record[recordIndex] = tempRecord;
					setSearchRecord(_record);
				} else {
					const _record = [...searchRecord];
					_record.splice(recordIndex, 1);
					setSearchRecord(_record);
				}
			} else {
				if (tempRecord.value != "") {
					const _record = [...searchRecord];
					_record.push(tempRecord);
					setSearchRecord(_record);
				}
			}
		}
	}, [tempRecord]);

	useEffect(() => {
		if (searchRecord?.length > 0) {
			let data: any = [];

			data = [...dataTable];
			console.log(searchRecord);
			const filteredData = data
				?.map((element: any) => {
					const bool = searchRecord.every((item: any) => {
						if (Array.isArray(element[item.columnKey])) {
							return element[item.columnKey].some((arrayItem: any) =>
								arrayItem.toLowerCase().includes(item.value.toLowerCase())
							);
						} else {
							return element[item.columnKey]
								?.toLowerCase()
								.includes(item.value.toLowerCase());
						}
					});
					if (bool) return element;
				})
				.filter((element: any) => element !== null && element !== undefined);
			setFilteredDataTable(filteredData);
		} else {
			setFilteredDataTable(null);
		}
	}, [searchRecord]);

	const { data: projectData, isLoading: isProjectLoading } = useQuery({
		queryKey: ["projects", currentTenantKey, projectCode],
		queryFn: async () => {
			const response = (await getProject(
				currentTenantKey,
				projectCode
			)) as TProjectData;
			return response ?? null;
		},
		staleTime: 5 * 60 * 1000,
		enabled: !!projectCode && !!currentTenantKey,
	});

	// Update project info and related data when projectData is loaded
	useEffect(() => {
		if (projectData) {
			setProjectTitle(projectData.project_name);
			setProjectInfo(projectData);
			setTaskList((projectData as any).tasks || null);
			context.setCurrentProject(projectData);

			// Process role assignments
			const newDataTable = projectData.role_assignments
				? [...projectData.role_assignments]
				: [];
			const projectManagerExists = newDataTable?.find((role: any) => {
				return role.role_id == "project_manager";
			});

			if (
				projectData.project_manager &&
				projectData.project_manager.length > 0 &&
				!projectManagerExists
			) {
				newDataTable.push({
					role_id: "project_manager",
					role: "Project Manager",
					appointee: [...projectData.project_manager],
				});
			}

			const generalMemberExists = newDataTable?.find((role: any) => {
				return role.role_id === "general_member";
			});

			if (
				projectData.general_members &&
				projectData.general_members.length > 0 &&
				!generalMemberExists
			) {
				newDataTable.push({
					role_id: "general_member",
					role: "General Member",
					appointee: [projectData.general_members.join(", ")],
				});
			}

			const mainApproverExists = newDataTable?.find((role: any) => {
				return role.role_id === "main_approver";
			});

			if (
				projectData.main_approver &&
				projectData.main_approver.length > 0 &&
				!mainApproverExists
			) {
				newDataTable.push({
					role_id: "main_approver",
					role: "Main Approver",
					appointee: [projectData.main_approver.join(", ")],
				});
			}

			const secondaryApproverExists = newDataTable?.find((role: any) => {
				return role.role_id === "secondary_approver";
			});

			if (
				projectData.secondary_approver &&
				projectData.secondary_approver.length > 0 &&
				!secondaryApproverExists
			) {
				newDataTable.push({
					role_id: "secondary_approver",
					role: "Secondary Approver",
					appointee: [projectData.secondary_approver.join(", ")],
				});
			}

			setDataTable(newDataTable);
		} else if (projectData === null && !isProjectLoading) {
			setProjectInfo(null);
		}
	}, [projectData, isProjectLoading]);

	// Load filters from localStorage
	useEffect(() => {
		if (localStorage.getItem("selectedFilters")) {
			const selectedFiltersStore: any = localStorage.getItem("selectedFilters");
			const selectedFiltersStoreJson = JSON.parse(selectedFiltersStore);
			setSelectedFilters(selectedFiltersStoreJson);
			setFilterMode(true);
		}
	}, []);

	// Fetch wells and operations data when project is loaded
	useEffect(() => {
		if (!permissionsInitialized) return;
		if (examinerOnlyView) {
			// The lean view never loads wells or operations (the server denies them).
			setIsLoading(false);
			return;
		}
		if (projectData && currentTenantKey) {
			Emitter.emit("loading", {
				name: "project-campaign-overview",
				isLoading: true,
			});

			setIsLoading(true);

			// get related wells data and operations data and combine them
			getWells(currentTenantKey, { project_ids: projectData._id })
				.then(async (wellData: any) => {
					const operationsData: any = [];

					for (const well of wellData) {
						try {
							const operations: any = await getOperations(currentTenantKey, {
								project: projectData._id,
								well_id: well._id,
							});
							if (operations?.length > 0) {
								operations.map((operation: any) => {
									operationsData.push({
										...operation,
										...well,
									});
								});
							}
						} catch (err) {
							console.log(err);
						}
					}

					setWellList(operationsData);
				})
				.catch((e: any) => {
					console.log(e);
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "project-campaign-overview",
						isLoading: false,
					});
					setIsLoading(false);
				});
		}
	}, [
		projectData,
		currentTenantKey,
		projectCode,
		examinerOnlyView,
		permissionsInitialized,
	]);

	const hasSelectedFilters = () => {
		// Check if any of the filter categories have selected values
		return Object.values(selectedFilters).some(
			(values: any) => values.length > 0
		);
	};
	const handleMenuClick = (field: any, selectedValues: any[]) => {
		// console.log(`Selected: ${field} - ${selectedValues}`);
		// Update the selected filters state
		setSelectedFilters({ ...selectedFilters, [field]: selectedValues });

		localStorage.setItem(
			"selectedFilters",
			JSON.stringify({ ...selectedFilters, [field]: selectedValues })
		);
	};

	const handleClearAllFilters = () => {
		// Reset all filters to empty arrays
		setSelectedFilters({
			location: [],
			well_purpose: [],
			well_status: [],
		});
		setFilterMode(false);

		// Clear session storage for filter values and checkboxes
		localStorage.removeItem("selectedFilters");

		// Object.keys(selectedFilters).forEach((field) => {
		// 	const storageKey = generateStorageKey(field);
		// 	localStorage.removeItem(storageKey);
		// });

		setTimeout(() => {
			setFilterMode(true);
		}, 5);
	};

	const selectWell = (wellKey: any, well: any) => {
		if (selectedWell != wellKey) {
			setSelectedWell(wellKey);
			setSelectedWellInfo(well);
			const selectedElement = document.getElementById(`project-${wellKey}`);

			setTimeout(() => {
				if (selectedElement) {
					selectedElement.scrollIntoView({ behavior: "smooth" });
				}
			}, 1);
		} else {
			setSelectedWell(null);
			setSelectedWellInfo(null);
		}
	};

	const renderDetailData = () => {
		return (
			<div
				className="meta-data-container"
				style={{ gridTemplateColumns: "repeat(3, auto)" }}
			>
				{projectTemplateExample.map((element: any) => (
					<div
						style={{
							display: "grid",
							gridTemplateRows: "20px auto",
							gridRow: element.label === "Country" ? "span 2" : undefined,
						}}
					>
						<span className="panel-detail-label">{element.label}</span>
						<span className="panel-item-content">
							{setLookupData(context.allData, projectInfo, element.key)}
						</span>
					</div>
				))}
				{/* Project Tags */}
				{renderProjectTags()}
			</div>
		);
	};

	const renderProjectTags = () => {
		return (
			<div
				style={{
					display: "grid",
					rowGap: "6px",
					gridTemplateRows: "auto auto",
					gridColumn: "span 3",
				}}
			>
				<span
					className="panel-detail-label"
					style={{
						// color: "rgba(255, 255, 255, 0.45)",
						display: "flex",
						justifyContent: "flex-start",
						fontSize: "12px",
					}}
				>
					Project tags
				</span>
				<span
					style={{
						display: "flex",
						flexWrap: "wrap",
						fontSize: "14px",
						rowGap: "10px",
					}}
				>
					{setLookupData(context.allData, projectInfo, "project_tags")}
				</span>
			</div>
		);
	};

	const listOperations = () => {
		const renderPurpose = (
			operation: any,
			purpose: string,
			purposeType: string
		) => {
			switch (purpose) {
				case "In progress":
					return (
						<div className="project-status-item status-counter-in-progress">
							<span>
								{purpose} ({purposeType})
							</span>
						</div>
					);

				case "New":
					return (
						<div className="project-status-item status-counter-new">
							<span>New ({purposeType})</span>
						</div>
					);

				case "Resolved":
					return (
						<div className="project-status-item status-counter-completed">
							<span>Resolved ({purposeType})</span>
						</div>
					);
				default:
					if (purpose) {
						return (
							<div className="project-status-item status-counter-in-progress">
								<span>
									{(() => {
										const result = setLookupData(
											context.allData,
											operation,
											purpose
										);
										if (React.isValidElement(result)) {
											return <span style={{ color: "#fff" }}>N/A</span>;
										}
										return result;
									})()}{" "}
									{operation?.[purposeType] && (
										<>
											({setLookupData(context.allData, operation, purposeType)})
										</>
									)}
								</span>
							</div>
						);
					} else {
						return <></>;
					}
			}
		};
		const renderStatus = (
			operation: any,
			status: string,
			statusType: string
		) => {
			switch (status) {
				case "In progress":
					return (
						<div className="project-status-item status-counter-in-progress">
							<span>In Progress</span>
						</div>
					);

				case "New":
					return (
						<div className="project-status-item status-counter-new">
							<span>New</span>
						</div>
					);

				case "Resolved":
					return (
						<div className="project-status-item status-counter-completed">
							<span>Resolved</span>
						</div>
					);
				default:
					if (status) {
						return (
							<div className="project-status-item status-counter-in-progress">
								<span>
									{setLookupData(context.allData, operation, status)}{" "}
									{operation?.[statusType] && (
										<>
											({setLookupData(context.allData, operation, statusType)})
										</>
									)}
								</span>
							</div>
						);
					} else {
						return <></>;
					}
			}
		};

		const renderDetail = (operation: any) => {
			const showDetailInfo = () => {
				const infoKeyList: any = [
					{ label: "License", key: "license" },
					{ label: "Operation type", key: "operation_type" },
					{ label: "Location", key: "location" },
					{ label: "Est. duration (days)", key: "estimated_duration" },
				];

				return (
					<>
						{infoKeyList.map((infoKey: any) => {
							return (
								<Space direction="vertical" size={0}>
									<span
										style={{
											// color: "rgba(255,255,255,0.45)",
											opacity: 0.45,
											fontSize: "12px",
										}}
									>
										{infoKey.label}
									</span>
									<div
										style={{
											display: "flex",
											justifyContent: "flex-start",
											alignItems: "center",
											fontSize: "14px",
											minWidth: "100%",
											width: "0",
										}}
									>
										<Text
											title=""
											style={
												infoKey.key == "country"
													? {
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}
													: {}
											}
											ellipsis={{
												// onEllipsis: () => {},
												tooltip: (
													<span>{setWellContent(operation, infoKey.key)}</span>
												),
											}}
										>
											<span
												style={
													infoKey.key == "country"
														? {
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}
														: {}
												}
											>
												{setWellContent(operation, infoKey.key)}
											</span>
										</Text>
									</div>
									{/* <span>{setProjectContent(project, infoKey.key)}</span> */}
								</Space>
							);
						})}
					</>
				);
			};

			return (
				<div className="project-detailed-container">
					<Space direction="vertical" style={{ width: "100%" }} size={15}>
						<Space
							direction="vertical"
							size={0}
							style={{ display: "flex", flexDirection: "column" }}
						>
							<span
								className="project-operation-title"
								style={{
									opacity: 0.45,
									lineHeight: "25px",
								}}
							>
								Operation title xxx
							</span>
							<div style={{ lineHeight: "25px" }}>
								<span
									className={"project-title-clickable"}
									style={{
										whiteSpace: "normal",
										display: "inline",
										overflowWrap: "break-word",
									}}
									onClick={() => {
										navigate(`${operation.operation_code}`, {
											replace: false,
											state: {
												...operation,
												state: { ...operation, projectTitle, projectInfo },
											},
										});
									}}
									onContextMenu={(event: any) => {
										const location = `${window.location.href}/${operation.operation_code}`;
										handleContextMenu(event, { link: location });
									}}
								>
									{operation.operation_name}
								</span>
							</div>
						</Space>
						<div className="project-status">
							{renderPurpose(operation, "well_purpose", "well_purpose_type")}
							{renderStatus(operation, "well_status", "well_status_type")}
						</div>
						<div className="project-grid-container">{showDetailInfo()}</div>
					</Space>
				</div>
			);
		};

		const renderCompact = (operation: any) => {
			return (
				<div className="project-mini-container">
					<Space direction="vertical" size={9}>
						<Space
							direction="vertical"
							size={0}
							style={{ display: "flex", flexDirection: "column" }}
						>
							<span
								style={{
									opacity: 0.45,
									fontSize: "12px",
								}}
							>
								Operation title
							</span>
							<span
								className={"project-title-clickable"}
								style={{
									whiteSpace: "normal",
									display: "inline",
									overflowWrap: "break-word",
								}}
								onClick={() => {
									navigate(operation.operation_code, {
										replace: false,
										state: { ...operation, projectTitle, projectInfo },
									});
								}}
								onContextMenu={(event: any) => {
									const location = `${window.location.href}/${operation.operation_code}`;
									handleContextMenu(event, { link: location });
								}}
							>
								{operation?.operation_name}
							</span>
						</Space>
						<div className="project-status">
							{renderPurpose(operation, "well_purpose", "well_purpose_type")}
							{"  "}
							{renderStatus(operation, "well_status", "well_status_type")}
						</div>
					</Space>
				</div>
			);
		};
		const currentWellList = filteredWellList || wellList;
		if (currentWellList.length > 0) {
			return currentWellList?.map((element: any) => {
				return (
					<div
						id={`project-${element.operation_code}`}
						className={`overview-card-list-container`}
						onClick={() => {
							selectWell(element.operation_code, element);
						}}
					>
						{element.operation_code === selectedWell ? (
							<>{renderDetail(element)}</>
						) : (
							<>{renderCompact(element)}</>
						)}
					</div>
				);
			});
		} else {
			return <span>No data found.</span>;
		}
	};

	const setWellContent = (currentWellData: any, infoKey: any) => {
		let content = "-";
		if (currentWellData[infoKey]) {
			content = setLookupData(context.allData, currentWellData, infoKey);
			if (infoKey === "location") {
				content =
					currentWellData[infoKey].charAt(0).toUpperCase() +
						currentWellData[infoKey].slice(1) || "-";
			}
		}
		return content;
	};

	useEffect(() => {
		let filteredWellList = null;
		if (searchValue != "") {
			const filteredWell = wellList?.filter((well: any) =>
				well.operation_name?.toLowerCase().includes(searchValue.toLowerCase())
			);
			filteredWellList = filteredWell;
		} else {
			filteredWellList = wellList;
		}
		const check = Object.keys(selectedFilters).some(
			(filterKey: any) => selectedFilters?.[filterKey]?.length > 0
		);
		if (check && filteredWellList) {
			filteredWellList = filteredWellList.filter((well: any) =>
				Object.keys(selectedFilters).every((filterKey: any) => {
					if (selectedFilters[filterKey].length == 0) {
						return true;
					}
					return selectedFilters[filterKey].includes(well[filterKey]);
				})
			);
		}

		setFilteredWellList(filteredWellList);
	}, [searchValue, selectedFilters]);

	if (hasViewProjectPermission.isLoading) {
		return <></>;
	}

	if (!hasViewProjectPermission.hasPermission) {
		return (
			<NoAccess text="Oops, looks like you don't have the authorisation to view projects." />
		);
	}

	if (!projectInfo && !isProjectLoading && !isLoading) {
		return <NoAccess text="This project does not exist." />;
	}

	if (projectInfo && examinerOnlyView) {
		return <WellExaminationProjectView projectData={projectInfo} />;
	}

	const renderKanbanColumns = () => {
		const renderTasks = (phase: any) => {
			const currentTaskList = filteredTaskList || taskList;
		};
	};

	return (
		<>
			{projectInfo &&
				(projectInfo.project_scope === "multi-asset" ? (
					<MultiAssetProjectContainer projectData={projectInfo} />
				) : (
					<div
						className="generic-container"
						style={{ gap: "20px", overflow: "auto" }}
					>
						{/* Project Details Container */}
						<div style={{ width: "100%" }}>
							<div
								className="container-header"
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<div
									className="panel-info"
									onClick={() => {
										setProjectDetailsExpand(!projectDetailsExpand);
									}}
								>
									<span>Project details</span>
									{projectDetailsExpand
										? GetAntIcon("up")
										: GetAntIcon("downarrow")}
								</div>
								{/* Panel Buttons Container */}
								<div className="panel-main-buttons-container">
									{(hasEditProjectPermission || hasDeleteProjectPermission) && (
										<a
											onClick={() => {
												navigate("project-settings", {
													replace: false,
													state: { ...projectInfo },
												});
											}}
											onContextMenu={(event: any) => {
												const location = `${window.location.href}/project-settings`;
												handleContextMenu(event, { link: location });
											}}
										>
											{GetAntIcon("setting")} Project settings
										</a>
									)}
									<AuditLogButton
										moduleKey="project-delivered"
										handleClick={() => {
											setAuditOpen(true);
										}}
									/>
								</div>
							</div>
							{/* Expanded Project Details */}
							<div
								className="details-content project-details-container"
								style={{
									display: projectDetailsExpand ? "flex" : "none",
									flexWrap: "inherit",
									flexDirection: "row",
									alignItems: "flex-start",
									justifyContent: "space-between",
									padding: "30px",
									gap: 0,
									overflowX: "auto",
									maxHeight: "100%",
								}}
							>
								{/* Project Details Content */}
								<div className="project-details-content" ref={panelRef}>
									{renderDetailData()}
									<CloseProjectButton projectData={projectInfo} />
								</div>
								{/* Divider */}
								<Divider
									type="vertical"
									style={{ height: "100%", margin: "0px 20px" }}
								></Divider>
								<div
									style={{
										width: "708px",
										borderLeft: "1px solid rgba(255, 255, 255, 0.15)", // 0.15 = 15% opacity
										padding: "0px 20px 10px 20px",
									}}
								>
									{/* <Tabs items={[{ label: "Team roles", key: "team-roles" }]} /> */}
									<div
										id="table-container"
										style={{
											flex: 1,
											width: "100%",
											maxHeight: "100%",
											overflow: "hidden",
										}}
									>
										{/* Dimensions: {JSON.stringify(tableDimensions)} */}
										<Table
											rootClassName="m"
											pagination={false}
											scroll={{
												y: panelDimensions.height - 64.8,
												x: 0,
											}}
											dataSource={filteredDataTable || dataTable}
											columns={[
												{
													title: (
														<SearchButton
															handleSearch={handleSearch}
															headerName="Team role"
															columnKey="role"
															key={"y" + "role"}
														/>
													),
													dataIndex: "role", // tells the table to look for a role field in each row's data
													key: "role", // unique key for each row
													render: (text: any, record: any) => {
														// Check if the role is "general_member", then render as General Member
														if (record.role_id === "general_member") {
															return <span>General members</span>;
														}
														if (record.role_id === "main_approver") {
															return <span>Main Approver</span>;
														}
														if (record.role_id === "secondary_approver") {
															return <span>Secondary Approver</span>;
														}
														// The role name is stored on the record itself
														return <span>{record.role}</span>;
													},
												},
												{
													title: (
														<SearchButton
															handleSearch={handleSearch}
															headerName="Assigned to" // Column title
															columnKey="appointee" // Unique key for search
															key={"x" + "appointee"}
														/>
													),
													dataIndex: "appointee",
													render: (text: any, record: any) =>
														record["appointee"]?.map((name: any) => {
															return (
																<>
																	<span>{name}</span>

																	<br />
																</>
															);
														}),
													key: "appointee",
												},
											]}
										/>
									</div>
								</div>
							</div>
						</div>
						{/* Bottom Container */}
						{projectInfo.project_scope === "multi-asset" ? (
							<div
								className="main-container"
								style={{
									minHeight: "85%",
									flex: 1,
									width: "100%",
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<div className="project-header">
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											gap: "10px",
										}}
									>
										<TabButton
											identifier="kanban"
											label="Kanban"
											icon={GetAntIcon("project")}
											activePage={activePage}
											setActivePage={setActivePage}
										/>
										<TabButton
											identifier="gantt-chart"
											label="Gantt chart"
											icon={GetAntIcon("gantt")}
											activePage={activePage}
											setActivePage={setActivePage}
										/>
									</div>
									<div
										style={{
											display: "flex",
											flex: "1",
											justifyContent: "flex-end",
											alignItems: "center",
											gap: "15px",
										}}
									>
										<Input
											placeholder="Search"
											className="input-clearable-panel"
											style={{ maxWidth: "265px" }}
											onPressEnter={(
												e: React.KeyboardEvent<HTMLInputElement>
											) => {
												setSearchValue(e.currentTarget.value);
												e.currentTarget.blur();
											}}
											onChange={(e) => {
												if (e.target.value === "") {
													setSearchValue("");
												}
											}}
											allowClear
											prefix={<SearchOutlined />}
										/>
										<div style={{ display: "flex", flexDirection: "row" }}>
											<div
												className={
													filterMode || hasSelectedFilters()
														? "project-active-button"
														: ""
												}
												style={{ padding: "4px 10px", cursor: "pointer" }}
												onClick={() => {
													setFilterMode(!filterMode);
												}}
											>
												{GetAntIcon("filter")}
											</div>
										</div>
									</div>
								</div>
								<div
									className="generic-content project-overview-container"
									style={{
										flex: 1,
										display: "flex",
										flexWrap: "inherit",
										flexDirection: "column",
										alignItems: "flex-start",
										padding: 0,
										gap: 0,
										overflowX: "auto",
										maxHeight: "100%",
									}}
								></div>
							</div>
						) : (
							<div
								className="main-container"
								style={{
									minHeight: "85%",
									flex: 1,
									width: "100%",
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<div className="project-header">
									{wellExaminationApi ? (
										<div
											style={{
												display: "flex",
												flexDirection: "row",
												gap: "10px",
											}}
										>
											<TabButton
												identifier="overview"
												label="Map - Well location"
												icon={GetAntIcon("global")}
												activePage={singleAssetTab}
												setActivePage={setSingleAssetTab}
											/>
											<TabButton
												identifier="well-examination"
												label="Well examination"
												icon={GetAntIcon("file2")}
												activePage={singleAssetTab}
												setActivePage={setSingleAssetTab}
												tooltip={{
													title: wellExaminationDisabled
														? "Well examination documents are disabled"
														: "",
													placement: "top",
												}}
												disabled={wellExaminationDisabled}
											/>
										</div>
									) : (
										<span>Map - Well location</span>
									)}
									{singleAssetTab === "overview" && (
										<div
											style={{
												display: "flex",
												flex: "1",
												justifyContent: "flex-end",
												alignItems: "center",
												gap: "15px",
											}}
										>
											<Input
												placeholder="Search by operation"
												className="input-clearable-panel"
												style={{ maxWidth: "265px" }}
												onPressEnter={(
													e: React.KeyboardEvent<HTMLInputElement>
												) => {
													setSearchValue(e.currentTarget.value);
													e.currentTarget.blur();
												}}
												onChange={(e) => {
													if (e.target.value === "") {
														setSearchValue("");
													}
												}}
												allowClear
												prefix={<SearchOutlined />}
											/>
											<div style={{ display: "flex", flexDirection: "row" }}>
												{" "}
												<div
													className={
														filterMode || hasSelectedFilters()
															? "project-active-button"
															: ""
													}
													style={{ padding: "4px 10px", cursor: "pointer" }}
													onClick={() => {
														setFilterMode(!filterMode);
													}}
												>
													{GetAntIcon("filter")}
												</div>
											</div>
										</div>
									)}
								</div>
								{singleAssetTab === "overview" && (
									<div className="project-overview-container overview-content">
										<div
											className={`filter-area ${
												filterMode ? "show-filter" : "hide-filter"
											}`}
										>
											{filterMode && (
												<>
													<CheckboxDropdown
														options={[
															{
																label: "onshore",
																value: "onshore",
															},
															{ label: "offshore", value: "offshore" },
														]}
														onChange={(selectedValues) =>
															handleMenuClick("location", selectedValues)
														}
														onClear={""}
														defaultValue={selectedFilters.location}
														Key="location"
														label="Location"
													/>
													<CheckboxDropdown
														options={context?.allData?.well_purpose?.map(
															(wp: any) => ({
																label: wp.name,
																value: wp.uuid,
															})
														)}
														onChange={(selectedValues) =>
															handleMenuClick("well_purpose", selectedValues)
														}
														onClear={""}
														defaultValue={selectedFilters["well_purpose"]}
														Key="well_purpose"
														label="Well purpose"
													/>
													<CheckboxDropdown
														options={context?.allData?.well_status?.map(
															(ws: any) => ({
																label: ws.name,
																value: ws.uuid,
															})
														)}
														onChange={(selectedValues) =>
															handleMenuClick("well_status", selectedValues)
														}
														onClear={""}
														defaultValue={selectedFilters["well_status"]}
														Key="well_status"
														label="Well status"
													/>
													<div className="test">
														<Button type="link" onClick={handleClearAllFilters}>
															Reset All
														</Button>
													</div>
												</>
											)}
										</div>
										<div className="overview-map" ref={tableRef}>
											<Map
												projectList={filteredWellList || wellList}
												selectProject={selectWell}
												selectedProject={selectedWell}
												selectedProjectInfo={selectedWellInfo}
												keyType={"operation_code"}
											></Map>
											<div
												className="sub-container"
												style={{
													maxHeight: tableDimensions.height,
												}}
											>
												<div className="project-list-container">
													{wellList?.length > 0 ? (
														<Space
															className="project-list"
															direction="vertical"
														>
															{listOperations()}
														</Space>
													) : (
														<Space
															className="project-list"
															direction="vertical"
														>
															<span>
																This project does not have any operations yet.
															</span>
														</Space>
													)}
												</div>

												{/* </div> */}
											</div>
										</div>
									</div>
								)}
								{/* Scroll container stays mounted (display none) so the tab can enable itself while hidden */}
								{wellExaminationApi && (
									<div
										className="generic-content project-overview-container"
										style={{
											flex: 1,
											display:
												singleAssetTab === "well-examination" ? "flex" : "none",
											flexWrap: "inherit",
											flexDirection: "column",
											alignItems: "flex-start",
											padding: 0,
											gap: 0,
											overflowX: "auto",
											maxHeight: "100%",
										}}
									>
										<SharepointComponent
											setDisabled={setWellExaminationDisabled}
											type="well-examination"
											id={projectInfo.project_code}
											name={projectInfo.project_name}
											api={wellExaminationApi}
											visible={singleAssetTab === "well-examination"}
										/>
									</div>
								)}
							</div>
						)}

						<AuditLogModal
							label="Project"
							tenantKey={context?.currentTenantKey}
							identifierKeys={{ project_code: projectInfo?.project_code }}
							open={auditOpen}
							type={"project"}
							handleClose={() => {
								setAuditOpen(false);
							}}
						/>
					</div>
				))}
		</>
	);
}

export default ProjectCampaignOverview;
